import { makeStyles } from "tss-react/mui"
import { Box, Link, Stack } from "@mui/material"
import { LocalizedEnum } from "../../../../../modules/localization/components/AppLocalized"
import { OrganizerSelectedValue, SelectedMainUserState } from "../../../createOrder/createOrderTypes"
import { tailwindPalette } from "../../../../../modules/theme/tailwindPalette"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import {
    DeliveryTimesField,
    DeliveryFulfillmentType,
    DynamicConfigKey,
} from "../../../createOrder/components/form/formTypes"
import { formatEventTimeRange } from "./helpers"
import {
    SectionBlock,
    SectionItemData,
    SectionItemLabel,
    SectionItemLink,
    SectionSubTitle,
    SectionTitleDataContainer,
} from "./common/ReceiptPrimitives"

const useStyles = makeStyles()((theme) => ({
    container: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "1.5rem",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "1fr",
            gap: "0",
        },
    },
    box: {
        background: tailwindPalette.blue[50],
        [theme.breakpoints.down("sm")]: {
            background: "white",
            borderRadius: 0,
            padding: "2rem 1.25rem",
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
    },
    darkColor: {
        color: theme.palette.text.primary,
    },
    textWrap: {
        "& > p:first-of-type": {
            color: theme.palette.text.secondary,
        },
    },
    email: {
        color: tailwindPalette.slate[500],
    },
    rightAlignData: {
        textAlign: "right",
    },
}))

type DetailsProps = Readonly<{
    orderDetails: Record<string, any>
    mainUser: Partial<SelectedMainUserState> & { email?: string }
    resourceNames: string
}>

export function Details({ orderDetails, mainUser, resourceNames }: DetailsProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    const { deliveryDate } = orderDetails[DynamicConfigKey.MULTIPLE_DELIVERIES] as DeliveryTimesField
    const formattedDate =
        deliveryDate && dateTimeFactory.formatCustom(dateTimeFactory.fromISO(deliveryDate), "d. MMM yyyy, hh.mm a")
    const formattedMeetingTime = formatEventTimeRange(orderDetails["event"], dateTimeFactory)

    const deliveryType: DeliveryFulfillmentType = orderDetails[DynamicConfigKey.DELIVERY_TYPE]
    const organizers: OrganizerSelectedValue = orderDetails[DynamicConfigKey.MEETING_RESPONSIBLE]

    return (
        <Box className={classes.container}>
            <SectionBlock
                classNames={classes.box}
                translationKey="confirm-order-dialog-meeting-details"
                title="Meeting details"
            >
                <Stack gap=".5rem">
                    {orderDetails[DynamicConfigKey.MEETING_NAME] && (
                        <SectionSubTitle>{orderDetails[DynamicConfigKey.MEETING_NAME]}</SectionSubTitle>
                    )}
                    {formattedMeetingTime && (
                        <SectionTitleDataContainer>
                            <SectionItemLabel translationKey="confirm-order-dialog-meeting-time">
                                Meeting time:
                            </SectionItemLabel>
                            <SectionItemData>{formattedMeetingTime}</SectionItemData>
                        </SectionTitleDataContainer>
                    )}
                    {formattedDate && (
                        <SectionTitleDataContainer>
                            <SectionItemLabel translationKey="confirm-order-dialog-delivery-date">
                                Delivery date:
                            </SectionItemLabel>
                            <SectionItemData>{formattedDate}</SectionItemData>
                        </SectionTitleDataContainer>
                    )}
                    {deliveryType && (
                        <SectionTitleDataContainer>
                            <SectionItemLabel translationKey="confirm-order-dialog-delivery-location">
                                Delivery location:
                            </SectionItemLabel>
                            <SectionItemData>
                                {deliveryType === DeliveryFulfillmentType.DELIVERY ? (
                                    !!orderDetails[DynamicConfigKey.OTHER_DELIVERY_LOCATION] ? (
                                        orderDetails[DynamicConfigKey.OTHER_DELIVERY_LOCATION]
                                    ) : (
                                        resourceNames
                                    )
                                ) : (
                                    <LocalizedEnum
                                        base="confirm-order-dialog-delivery-location-mode"
                                        enumValue={deliveryType}
                                    >
                                        <>{deliveryType}</>
                                    </LocalizedEnum>
                                )}
                            </SectionItemData>
                        </SectionTitleDataContainer>
                    )}
                    {!!orderDetails[DynamicConfigKey.NUMBER_OF_SEATS] && (
                        <SectionTitleDataContainer>
                            <SectionItemLabel translationKey="confirm-order-dialog-seats">Seats:</SectionItemLabel>
                            <SectionItemData>{orderDetails[DynamicConfigKey.NUMBER_OF_SEATS]}</SectionItemData>
                        </SectionTitleDataContainer>
                    )}
                    {orderDetails["priceList"]?.merchantName && (
                        <SectionTitleDataContainer>
                            <SectionItemLabel translationKey="confirm-order-dialog-merchant">
                                Merchant:
                            </SectionItemLabel>
                            <SectionItemData>{orderDetails["priceList"]?.merchantName}</SectionItemData>
                        </SectionTitleDataContainer>
                    )}
                </Stack>
            </SectionBlock>

            <SectionBlock classNames={classes.box} translationKey="confirm-order-dialog-contact" title="Contact">
                <Stack gap={1}>
                    {(organizers?.name || organizers?.email) && (
                        <SectionTitleDataContainer classNames={classes.rightAlignData}>
                            <SectionItemLabel translationKey="confirm-order-dialog-host">Host:</SectionItemLabel>
                            <Box>
                                <SectionItemData>{organizers.name}</SectionItemData>
                                {organizers?.email && (
                                    <SectionItemLink href={`mailto:${organizers.email}`}>
                                        {organizers.email}
                                    </SectionItemLink>
                                )}
                            </Box>
                        </SectionTitleDataContainer>
                    )}
                    <SectionTitleDataContainer classNames={classes.rightAlignData}>
                        <SectionItemLabel translationKey="confirm-order-dialog-ordered-by">
                            Ordered by:
                        </SectionItemLabel>
                        <Box>
                            <SectionItemData>{mainUser.displayName}</SectionItemData>
                            {mainUser?.email && (
                                <SectionItemLink href={`mailto:${mainUser.email}`}>{mainUser.email}</SectionItemLink>
                            )}
                        </Box>
                    </SectionTitleDataContainer>
                </Stack>
            </SectionBlock>
        </Box>
    )
}
